import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
// eslint-disable-next-line
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { HelpTooltipIcon } from 'components/elements/Help/HelpTooltipIcon';
import TimeSeriesBarGraph from 'components/structure/Analytics/TimeSeriesBarGraph';
import { PeakData, PeakSpaceTable } from 'components/structure/Space/SpaceAnalytics/PeakSpaceTable';
import {
  PopularityData,
  SpacePopularityTable
} from 'components/structure/Space/SpaceAnalytics/SpacePopularityTable';
import ExportSpaceCSV from 'components/structure/Space/SpaceForms/exportSpaceCSV';
import React, { useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import SpaceService, {
  AggregatedUsageResponse,
  Aggregation,
  BuildingUsageResponse,
  SpaceProps
} from 'services/hooks/space-service';
import { containerStyle } from 'utils/styles';
import { formatDateToString } from 'utils/time';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

function getPeakUsageForAllSpaces(
  spaces: SpaceProps[] | undefined,
  aggregatedUtilization?: AggregatedUsageResponse
): PeakData[] {
  if (!spaces || !aggregatedUtilization) {
    return [];
  }

  return spaces
    .flatMap((space) => {
      if (!space.uuid) return []; // Skip processing if space UUID is undefined

      const utilizationData = aggregatedUtilization.find((item) =>
        item.hasOwnProperty(space.uuid ?? '')
      );
      if (!utilizationData) return []; // Skip if no usage data is found for the space

      const data = utilizationData[space.uuid];
      const peakIndex = data.y.reduce((iMax, x, i, arr) => (x > arr[iMax] ? i : iMax), 0);
      const capacity = Number(space.capacity); // Ensure capacity is treated as a number

      // Construct the PeakData object, including capacity
      return [
        {
          name: space.name,
          occupancy: data.y[peakIndex],
          time: data.x[peakIndex],
          capacity: isNaN(capacity) ? 0 : capacity // Provide a fallback value if capacity is not a valid number
        }
      ];
    })
    .filter((item) => item !== null); // This is technically unnecessary due to the `return [];` in our map function but kept for clarity
}

function getSpaceCapacity(
  allSpacesData: SpaceProps[] | undefined,
  selectedSpaceUuid: string
): number {
  let selectedSpaceCapacity = 0;

  if (!!allSpacesData && !!selectedSpaceUuid) {
    selectedSpaceCapacity = Number(
      allSpacesData.find((space) => space.uuid === selectedSpaceUuid)?.capacity ?? 0
    );
  }

  return selectedSpaceCapacity;
}

function getAverageOccupancyForSpace(
  spaceUuid: string,
  aggregatedUtilization?: AggregatedUsageResponse
): number {
  if (!aggregatedUtilization) {
    return 0;
  }

  // Attempt to find the utilization data for the specified space UUID
  const targetData = aggregatedUtilization.find((item) => item.hasOwnProperty(spaceUuid));

  if (!targetData) {
    return 0; // Returning 0 or an indication that no data was found for the given UUID
  }

  // Extract the y array for the given space UUID
  const yArray = targetData[spaceUuid].y;

  // Calculate the average occupancy if yArray is not empty
  if (yArray.length > 0) {
    const sum = yArray.reduce((acc, curr) => acc + curr, 0);
    const average = sum / yArray.length;
    return average;
  } else {
    return 0; // Returning 0 as there's no occupancy data to calculate an average
  }
}

function generatePopularityRows(
  spaces?: SpaceProps[],
  aggregatedUtilization?: AggregatedUsageResponse
): PopularityData[] {
  const rows: PopularityData[] = [];

  if (!!spaces) {
    spaces.forEach((space) => {
      if (space.uuid) {
        rows.push({
          name: space.name,
          avgOccupancy: getAverageOccupancyForSpace(space.uuid, aggregatedUtilization),
          capacity: Number(space.capacity)
        });
      }
    });
  }

  return rows;
}

function generateUtilizationRows(
  spaces: SpaceProps[],
  usage?: AggregatedUsageResponse
): AggregatedUsageResponse {
  if (!spaces || !usage) {
    return [];
  }

  // Filter out spaces without a UUID
  const validSpaces = spaces.filter((space) => !!space.uuid);

  // Initialize an empty array for the final response
  const utilization: AggregatedUsageResponse = [];

  validSpaces.forEach((space) => {
    // Assuming space.uuid is defined based on the above filter
    const usageItem = usage.find((item) => item.hasOwnProperty(space.uuid ?? ''));

    if (usageItem && !!space.uuid) {
      // Retrieve the existing usage data for the current space
      const data = usageItem[space.uuid];

      // Populate the y values
      const uniqueOccupants: number[] = data.y.map((val) => val);

      // Construct and push the new item to the utilization array
      utilization.push({
        [space.uuid]: {
          x: data.x,
          y: uniqueOccupants
        }
      });
    }
  });

  return utilization;
}

function getSelectedSpaceGraphData(
  aggregatedUsageResponse: AggregatedUsageResponse | undefined,
  selectedSpaceUuid: string
): { x: string[]; y: number[] } | null {
  if (!aggregatedUsageResponse) {
    return null;
  }

  const targetData = aggregatedUsageResponse.find((item) => item.hasOwnProperty(selectedSpaceUuid));

  // If found, return the x and y arrays; otherwise, return null
  if (targetData) {
    return targetData[selectedSpaceUuid];
  } else {
    return null;
  }
}

const LS_KEY_BUILDING = 'bl.spaces.reporting.building';
const LS_KEY_SPACE = 'bl.spaces.reporting.space';
const ALL_SPACES = 'All Spaces';

export default function SpaceReporting() {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // get values from localstorage
  const lsSelectedBuildingUuid = localStorage.getItem(LS_KEY_BUILDING);
  const lsSelectedSpaceUuid = localStorage.getItem(LS_KEY_SPACE);

  const currentDate = new Date();
  const dateLastMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() - 1,
    currentDate.getDate()
  );

  const [startDate, setStartDate] = useState(dateLastMonth);
  const [endDate, setEndDate] = useState(currentDate);
  const [spacesInBuilding, setSpacesInBuilding] = useState<SpaceProps[]>([]);
  const [selectedBuildingUuid, setSelectedBuildingUuid] = useState<string>('');
  const [selectedSpaceUuid, setSelectedSpaceUuid] = useState<string>('');
  const [selectedAggregation, setSelectedAggregation] = useState<Aggregation>('hourly');

  // Buildings query
  const { isLoading: isBuildingsLoading, data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error('Problem fetching buildings.');
      }
    }
  );

  // Spaces query
  const { isLoading: isAllSpacesLoading, data: allSpacesData } = useQuery<SpaceProps[], Error>(
    'allSpaces',
    SpaceService.findAllSpaces,
    {
      onError: () => {
        toast.error('Problem fetching spaces.');
      }
    }
  );

  // Aggregated Space Usage query
  const { isLoading: isSpaceUsageLoading, data: spaceUsageData } = useQuery<
    AggregatedUsageResponse,
    Error
  >(
    ['spaceUsage', selectedBuildingUuid, selectedAggregation, startDate, endDate],
    () => {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);

      return SpaceService.spaceAggregatedUniqueOccupants({
        buildingUuid: selectedBuildingUuid,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(nextDay),
        aggregation: selectedAggregation
      });
    },
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate && !!selectedAggregation,
      onError: () => {
        toast.error('Problem fetching space usage.');
      }
    }
  );

  // Aggregated Building Usage query
  const { isLoading: isBuildingUsageLoading, data: buildingUsageData } = useQuery<
    BuildingUsageResponse,
    Error
  >(
    ['buldingUsage', selectedBuildingUuid, selectedAggregation, startDate, endDate],
    () => {
      const nextDay = new Date(endDate);
      nextDay.setDate(nextDay.getDate() + 1);

      return SpaceService.buildingAggregatedUniqueOccupants({
        buildingUuid: selectedBuildingUuid,
        startDate: formatDateToString(startDate),
        endDate: formatDateToString(nextDay),
        aggregation: selectedAggregation
      });
    },
    {
      enabled: !!selectedBuildingUuid && !!startDate && !!endDate && !!selectedAggregation,
      onError: () => {
        toast.error('Problem fetching building usage.');
      }
    }
  );

  useEffect(() => {
    if (!!selectedBuildingUuid && !!allSpacesData) {
      // filter the spaces that only apply to the building and store them in the state
      setSpacesInBuilding(
        allSpacesData.filter((space) => space.buildingUuid === selectedBuildingUuid)
      );
    }
  }, [selectedBuildingUuid, allSpacesData]);

  useEffect(() => {
    if (!!selectedBuildingUuid && !!spacesInBuilding.length) {
      // when the selected building changes, select its first space
      setSelectedSpaceUuid(spacesInBuilding[0].uuid ?? '');
    }
  }, [selectedBuildingUuid, spacesInBuilding]);

  /**
   * useEffects to restore the last selections from localstorage.
   */

  useEffect(() => {
    // If no building is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!buildingsData &&
      !selectedBuildingUuid &&
      lsSelectedBuildingUuid &&
      buildingsData.find((building) => building.uuid === lsSelectedBuildingUuid)
    ) {
      setSelectedBuildingUuid(lsSelectedBuildingUuid);
    }
  }, [buildingsData, lsSelectedBuildingUuid, selectedBuildingUuid]);

  useEffect(() => {
    // If no space is selected (e.g. initial load), use the last selection stored in localstorage (if it exists)
    if (
      !!spacesInBuilding &&
      !!spacesInBuilding.length &&
      !selectedSpaceUuid &&
      lsSelectedSpaceUuid &&
      (spacesInBuilding.find((space) => space.uuid === lsSelectedSpaceUuid) ||
        ALL_SPACES === lsSelectedSpaceUuid)
    ) {
      // set the local storage value, if none exists default to the first space
      setSelectedSpaceUuid(lsSelectedSpaceUuid || ALL_SPACES);
    }
  }, [spacesInBuilding, lsSelectedSpaceUuid, selectedSpaceUuid]);

  const handleStartDateChange = (date: Date | null) => {
    if (!!date && date <= endDate) {
      setStartDate(date);
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!!date && date >= startDate) {
      setEndDate(date);
    }
  };

  const handleBuildingChange = (event: SelectChangeEvent) => {
    const building = event.target.value as string;
    setSelectedBuildingUuid(building);
    // store the last selected building in localstorage
    localStorage.setItem(LS_KEY_BUILDING, building);
  };

  const handleSpaceChange = (event: SelectChangeEvent) => {
    const space = event.target.value as string;
    setSelectedSpaceUuid(space);
    // store the last selected space in localstorage
    localStorage.setItem(LS_KEY_SPACE, space);
  };

  const handleAggregationChange = (event: SelectChangeEvent) => {
    const aggregation = event.target.value as Aggregation;
    setSelectedAggregation(aggregation);
  };

  const aggregatedUtilization = generateUtilizationRows(spacesInBuilding, spaceUsageData);
  const selectedSpaceUsage =
    selectedSpaceUuid === ALL_SPACES
      ? buildingUsageData
      : getSelectedSpaceGraphData(aggregatedUtilization, selectedSpaceUuid);
  const popularityRows = generatePopularityRows(spacesInBuilding, spaceUsageData);
  const selectedSpaceCapacity = getSpaceCapacity(allSpacesData, selectedSpaceUuid);
  const peakRows = getPeakUsageForAllSpaces(spacesInBuilding, spaceUsageData);

  return (
    <div style={{ marginTop: 40 }}>
      <Grid container justifyContent="right" spacing={1} style={{ paddingBottom: 20 }}>
        <Grid item>
          <ExportSpaceCSV
            entityName={`Export CSV`}
            spacesInBuilding={spacesInBuilding}
            aggregatedUtilization={aggregatedUtilization}
            buildingsData={buildingsData}
            selectedBuildingUuid={selectedBuildingUuid}
            selectedAggregation={selectedAggregation}
            startDate={startDate}
            endDate={endDate}
          />
        </Grid>
      </Grid>

      <h1 style={{ paddingBottom: 20 }}>Spaces Reporting</h1>

      <Grid container justifyContent="right" spacing={1} style={{ paddingBottom: 20 }}>
        <Grid item xs={12} sm={5} style={{ width: 300, marginLeft: 10 }}>
          <FormControl fullWidth size="small" disabled={isBuildingsLoading}>
            <InputLabel>Building</InputLabel>
            <Select
              value={selectedBuildingUuid}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
              disabled={!buildingsData}
              onChange={handleBuildingChange}
              label="Building"
            >
              {!!buildingsData &&
                buildingsData
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((building) => {
                    return (
                      <MenuItem key={building.uuid} value={building.uuid}>
                        {building.name}
                      </MenuItem>
                    );
                  })}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          sm={2}
          style={{ maxWidth: 120, overflow: 'hidden', marginRight: 5, marginLeft: 10 }}
        >
          <InputLabel shrink style={{ marginBottom: -8 }}>
            Start Date
          </InputLabel>
          <DatePicker selected={startDate} onChange={handleStartDateChange} />
        </Grid>
        <Grid item xs={12} sm={2} style={{ maxWidth: 120, overflow: 'hidden' }}>
          <InputLabel shrink style={{ marginBottom: -8 }}>
            End Date
          </InputLabel>
          <DatePicker selected={endDate} onChange={handleEndDateChange} />
        </Grid>
        <Grid item xs={12} sm={3} style={{ width: 120, marginLeft: 10 }}>
          <FormControl fullWidth size="small">
            <Select
              value={selectedAggregation}
              onChange={handleAggregationChange}
              sx={{ borderRadius: '10px', backgroundColor: 'white' }}
            >
              <MenuItem value={'hourly'}>Hourly</MenuItem>
              <MenuItem value={'daily'}>Daily</MenuItem>
              <MenuItem value={'monthly'}>Monthly</MenuItem>
              <MenuItem value={'yearly'}>Yearly</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <div style={containerStyle}>
        <Grid container justifyContent="space-between" sx={{ padding: 2 }}>
          <Grid item xs={12} md={6}>
            <Grid container justifyContent="left">
              <Grid item>
                <h4 style={{ marginTop: 10, marginRight: 10 }}>Space Utilization Distribution</h4>
              </Grid>
              <Grid item>
                <HelpTooltipIcon
                  message={`The ${selectedAggregation} distribution of unique occupants who visited a space, averaged across the selected time span.`}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6} style={{ maxWidth: 333 }}>
            <FormControl fullWidth size="small" disabled={isAllSpacesLoading || isBuildingsLoading}>
              <Select
                value={selectedSpaceUuid}
                sx={{ borderRadius: '10px', backgroundColor: 'white' }}
                disabled={!spacesInBuilding || !spacesInBuilding.length}
                onChange={handleSpaceChange}
              >
                <MenuItem key={ALL_SPACES} value={ALL_SPACES}>
                  {ALL_SPACES}
                </MenuItem>
                {!!spacesInBuilding &&
                  spacesInBuilding
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((space) => {
                      return (
                        <MenuItem key={space.uuid} value={space.uuid}>
                          {space.name}
                        </MenuItem>
                      );
                    })}
              </Select>
              {!!selectedSpaceCapacity && (
                <FormHelperText>Capacity: {selectedSpaceCapacity}</FormHelperText>
              )}
              {!isAllSpacesLoading &&
                !isBuildingsLoading &&
                (!spacesInBuilding || !spacesInBuilding.length) && (
                  <FormHelperText>No spaces available for selected building</FormHelperText>
                )}
            </FormControl>
          </Grid>
        </Grid>

        <TimeSeriesBarGraph
          x={selectedSpaceUsage?.x ?? []}
          y={selectedSpaceUsage?.y ?? []}
          loading={isSpaceUsageLoading || isBuildingUsageLoading}
          aggregation={selectedAggregation}
          noDataHint="Consider updating your building, space, and date filters"
          yAxisLabel="Avg. unique occupants"
        />
      </div>

      <Grid container wrap={isDesktop ? 'nowrap' : 'wrap'} gap={2} style={{ width: '100%' }}>
        <Grid item xs={12} md={7} style={containerStyle}>
          <Grid container justifyContent="left">
            <Grid item>
              <h4 style={{ margin: 10 }}>Peak Space Utilization</h4>
            </Grid>
            <Grid item>
              <HelpTooltipIcon message="The time at which spaces were the busiest." />
            </Grid>
          </Grid>
          <PeakSpaceTable
            peakRows={peakRows}
            loading={isSpaceUsageLoading}
            aggregation={selectedAggregation}
          />
        </Grid>
        <Grid item xs={12} md={5} style={containerStyle}>
          <Grid container justifyContent="left">
            <Grid item>
              <h4 style={{ margin: 10 }}>Average Space Utilization</h4>
            </Grid>
            <Grid item>
              <HelpTooltipIcon message="The average number of unique occupants who visited a space." />
            </Grid>
          </Grid>
          <SpacePopularityTable
            popularityRows={popularityRows}
            loading={isSpaceUsageLoading}
            aggregation={selectedAggregation}
          />
        </Grid>
      </Grid>
    </div>
  );
}
